import {
  Button,
  Checkbox,
  Container,
  Divider,
  Grid,
  Group,
  Paper,
  Select,
  SelectItem,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { FormList, formList, joiResolver, useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import React, { useEffect, useState } from 'react';
import { Check, X } from 'tabler-icons-react';
import { cardTypesData } from '../../../data/card-types.data.array';
import { CurrenciesData } from '../../../data/currencies-data.array';
import { MidFileNames } from '../../../enums/mid-file-names.enum';
import { OpenComponent } from '../../../enums/misc.enum';
import { removeEmptyPropertiesWithNested } from '../../../helpers/filters.helpers';
import useAuth from '../../../hooks/useAuth';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { UpdateMIDsSchema } from '../../../schemas/update-mids.schema';
import { MidFileMetaData } from '../../../type/admin.types';
import { ErrorResponse } from '../../../type/error-response.types';
import { UpdateFormFile } from '../../../type/file.types';
import {
  RetrieveMIDResponse,
  UpdateMIDsRequest,
  UpdateMIDsRequestFrontend,
} from '../../../type/mids.types';
import { BasicSwitches } from '../../../type/switch.types';
import BackButton from '../../buttons/back-button.component';
import UploadButton from '../../buttons/file-upload.button/upload.button';
import SkeletonUpdateMIDs from './skeleton-loading/skeleton-update-mids.component';

function UpdateMIDs(props: Props) {
  const { midId, setComponentManager, setRefreshTable } = props;
  const [switchOptions, setSwitchOptions] = useState<SelectItem[]>([]);
  const { auth } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const [switchIsLoading, setSwitchIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [showPEMError, setShowPEMError] = useState(false);
  const form = useForm<UpdateMIDsRequestFrontend>({
    initialValues: {
      midName: '',
      switchId: undefined,
      cardTypeId: undefined,
      currencyId: undefined,
      isActive: true,

      xpayMarkup: 0,
      acquirerBuyRate: 0,
      declineRate: 0,
      xpayPerTransactionAddOn: 0,
      clientPerTransactionAddOn: 0,
      fraudControlFee: 0,
      preAuthRate: 0,
      captureRate: 0,
      clientMarkup: 0,
      refundRate: 0,
      voidRate: 0,
      cashBackRate: 0,
      balanceInquiryRate: 0,
      // finalMerchantRate: '',

      param2: undefined,
      param3: undefined,
      param6: undefined,
      mcc: '',
      pemFile: formList([]),
    },
    validate: joiResolver(UpdateMIDsSchema),
  });

  const getFiles = (): File[] => {
    let files: File[] = [];

    if (form.values.pemFile && form.values.pemFile.length > 0) {
      files = form.values.pemFile.map((file) => file.file);
    }

    return files;
  };

  const setFiles = (data: MidFileMetaData[]): FormList<UpdateFormFile> => {
    const files = formList<UpdateFormFile>(
      data
        .filter((file) => file.fileType === MidFileNames.MID_PEM)
        .map((file) => {
          return {
            id: file.id,
            file: new File([], file.name),
          };
        })
    );

    return files;
  };

  useEffect(() => {
    const retrieveSwitches = async () => {
      try {
        setSwitchIsLoading(true);
        const res = await axiosPrivate.get('web/select/switches');
        const basicSwitches: BasicSwitches[] = res.data.items;
        const options: SelectItem[] = basicSwitches.map((item) => {
          return {
            label: item.switchName,
            value: item.switchId.toString(),
          };
        });
        setSwitchOptions(options);
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-load-switches',
          message:
            error.error ||
            err?.response?.data ||
            'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        setSwitchIsLoading(false);
      }
    };
    const fetchMID = async () => {
      try {
        setIsInitialLoading(true);
        const res = await axiosPrivate.get(`web/mids/${midId}`);
        const mid: RetrieveMIDResponse = res.data;

        form.setValues({
          midName: mid.midName,
          switchId: mid.switch,
          cardTypeId: mid.cardType,
          currencyId: mid.currency,
          isActive: mid.isActive,
          xpayMarkup: mid.rates.xpayMarkup,
          acquirerBuyRate: mid.rates.acquirerBuyRate,
          declineRate: mid.rates.declineRate,
          xpayPerTransactionAddOn: mid.rates.xpayPerTransactionAddOn,
          clientPerTransactionAddOn: mid.rates.clientPerTransactionAddOn,
          fraudControlFee: mid.rates.fraudControlFee,
          preAuthRate: mid.rates.preAuthRate,
          captureRate: mid.rates.captureRate,
          clientMarkup: mid.rates.clientMarkup,
          refundRate: mid.rates.refundRate,
          voidRate: mid.rates.voidRate,
          cashBackRate: mid.rates.cashBackRate,
          balanceInquiryRate: mid.rates.balanceInquiryRate,
          param2: mid.params?.param2 ?? undefined,
          param3: mid.params?.param3 ?? undefined,
          param6: mid.params?.param6 ?? undefined,
          mcc: mid.params?.mcc ?? '',
          // finalMerchantRate: mid.rates.finalMerchantRate,
          pemFile: setFiles(mid.files ?? []),
        });
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;
        showNotification({
          id: 'failed-to-fetch-mid',
          message:
            error.error ||
            err?.response?.data ||
            'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        setIsInitialLoading(false);
      }
    };

    retrieveSwitches();
    fetchMID();
  }, []);

  const handleSubmit = async () => {
    if (form.values.switchId === 6 && form.values.pemFile.length === 0) {
      setShowPEMError(true);
      return;
    }

    const request: UpdateMIDsRequest = {
      midId: midId,
      midName: form.values.midName,
      cardTypeId: form.values.cardTypeId!,
      currencyId: form.values.currencyId!,
      isActive: form.values.isActive,

      params: {
        param2: form.values.param2,
        param3: form.values.param3,
        param6: form.values.param6,
        mcc: form.values.mcc,
      },

      rates: {
        xpayMarkup: form.values.xpayMarkup,
        acquirerBuyRate: form.values.acquirerBuyRate,
        declineRate: form.values.declineRate,
        xpayPerTransactionAddOn: form.values.xpayPerTransactionAddOn,
        clientPerTransactionAddOn: form.values.clientPerTransactionAddOn,
        fraudControlFee: form.values.fraudControlFee,
        preAuthRate: form.values.preAuthRate,
        captureRate: form.values.captureRate,
        clientMarkup: form.values.clientMarkup,
        refundRate: form.values.refundRate,
        voidRate: form.values.voidRate,
        cashBackRate: form.values.cashBackRate,
        balanceInquiryRate: form.values.balanceInquiryRate,
      },
    };

    const cleanedRequest = removeEmptyPropertiesWithNested(request);

    try {
      const formData = new FormData();

      const { pemFile } = form.values;

      const file = pemFile[0].file;

      formData.append(MidFileNames.MID_PEM, new Blob([file]), file.name);

      const response = await axiosPrivate.post(
        `/web/mids/${midId}/files`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );

      const midFileMetaData: MidFileMetaData[] = response.data?.files;


      cleanedRequest.isFilesRetained = response.data?.isRetained;
      if (!response.data?.isRetained) {
        cleanedRequest.files = midFileMetaData;
      }


      setIsLoading(true);
      await axiosPrivate.put(`web/mids`, cleanedRequest);

      showNotification({
        id: 'successfully-updated-mids',
        message: 'Successfully updated MID',
        icon: <Check size={16} />,
        color: 'teal',
        autoClose: 6000,
      });
      setRefreshTable((prev) => !prev);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-update-mid',
        message:
          error.error || err?.response?.data || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getDynamicFilter = () => {
    let filters: JSX.Element = <></>;
    filters = (
      <>
        {filters}
        <Grid.Col xs={12} sm={6}></Grid.Col>
      </>
    );

    return <Grid>{filters}</Grid>;
  };

  return (
    <div style={{ flex: 1, margin: 0 }}>
      <BackButton
        handleClick={() => {
          setComponentManager(OpenComponent.HOME);
        }}
      />
      <Title order={2} my='lg'>
        Update MIDs
      </Title>
      <Paper
        withBorder
        radius='md'
        shadow='lg'
        style={{
          flex: 1,
          height: '100%',
          padding: '20px 0',
        }}
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Container p='lg' fluid>
            {isInitialLoading ? (
              <SkeletonUpdateMIDs />
            ) : (
              <>
                <Grid>
                  <Grid.Col xs={12} sm={6}>
                    <TextInput
                      label={label.midName}
                      placeholder={placeHolder.midName}
                      {...form.getInputProps('midName')}
                    />
                  </Grid.Col>
                  <Grid.Col
                    xs={12}
                    sm={6}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                    }}
                  >
                    <Checkbox
                      label='Is active'
                      {...form.getInputProps('isActive', {
                        type: 'checkbox',
                      })}
                    />
                  </Grid.Col>
                  {/* empty */}

                  <Grid.Col xs={12} sm={4}>
                    <Select
                      label={label.switch}
                      placeholder={placeHolder.switch}
                      value={
                        switchIsLoading
                          ? 'Loading...'
                          : switchOptions.length === 0
                            ? 'No Data'
                            : form.values.switchId?.toString()
                      }
                      data={
                        switchIsLoading
                          ? ['Loading...']
                          : switchOptions.length === 0
                            ? ['No Data']
                            : switchOptions
                      }
                      disabled
                      error={form.errors.switchId}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={4}>
                    <Select
                      label={label.cardType}
                      placeholder={placeHolder.cardType}
                      data={cardTypesData}
                      value={form.values.cardTypeId?.toString()}
                      onChange={(value) => {
                        form.setFieldValue('cardTypeId', Number(value));
                      }}
                      error={form.errors.cardTypeId}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={4}>
                    <Select
                      label={label.currency}
                      placeholder={placeHolder.currency}
                      data={CurrenciesData}
                      value={form.values.currencyId?.toString()}
                      onChange={(value) => {
                        form.setFieldValue('currencyId', Number(value));
                      }}
                      error={form.errors.currencyId}
                    />
                  </Grid.Col>

                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.param3}
                      placeholder={placeHolder.param3}
                      {...form.getInputProps('param3')}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.param2}
                      placeholder={placeHolder.param2}
                      {...form.getInputProps('param2')}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.param6}
                      placeholder={placeHolder.param6}
                      {...form.getInputProps('param6')}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.mcc}
                      placeholder={placeHolder.mcc}
                      {...form.getInputProps('mcc')}
                    />
                  </Grid.Col>

                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.xpayMarkup}
                      placeholder={placeHolder.xpayMarkup}
                      {...form.getInputProps('xpayMarkup')}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.acquirerBuyRate}
                      placeholder={placeHolder.acquirerBuyRate}
                      {...form.getInputProps('acquirerBuyRate')}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.decline}
                      placeholder={placeHolder.decline}
                      {...form.getInputProps('declineRate')}
                    />
                  </Grid.Col>

                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.xpayPerTransactionAddOn}
                      placeholder={placeHolder.xpayPerTransactionAddOn}
                      {...form.getInputProps('xpayPerTransactionAddOn')}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.clientPerTransactionAddOn}
                      placeholder={placeHolder.clientPerTransactionAddOn}
                      {...form.getInputProps('clientPerTransactionAddOn')}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.preAuthRate}
                      placeholder={placeHolder.preAuthRate}
                      {...form.getInputProps('preAuthRate')}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.captureRate}
                      placeholder={placeHolder.captureRate}
                      {...form.getInputProps('captureRate')}
                    />
                  </Grid.Col>

                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.clientMarkup}
                      placeholder={placeHolder.clientMarkup}
                      {...form.getInputProps('clientMarkup')}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.refundRate}
                      placeholder={placeHolder.refundRate}
                      {...form.getInputProps('refundRate')}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.voidRate}
                      placeholder={placeHolder.voidRate}
                      {...form.getInputProps('voidRate')}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={4}>
                    <TextInput
                      label={label.fraudControlFee}
                      placeholder={placeHolder.fraudControlFee}
                      {...form.getInputProps('fraudControlFee')}
                    />
                  </Grid.Col>

                  <Grid.Col xs={12} sm={6}>
                    <TextInput
                      label={label.cashBackRate}
                      placeholder={placeHolder.cashBackRate}
                      {...form.getInputProps('cashBackRate')}
                    />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={6}>
                    <TextInput
                      label={label.balanceInquiryRate}
                      placeholder={placeHolder.balanceInquiryRate}
                      {...form.getInputProps('balanceInquiryRate')}
                    />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <TextInput
                      label={label.finalMerchantRate}
                      placeholder={placeHolder.finalMerchantRate}
                      disabled
                      // defaultValue={form.values.finalMerchantRate}
                      value={
                        (
                          Number(form.values.acquirerBuyRate) +
                          Number(form.values.xpayMarkup) +
                          Number(form.values.clientMarkup)
                        ).toFixed(2) +
                        '% + ' +
                        (
                          Number(form.values.xpayPerTransactionAddOn) +
                          Number(form.values.clientPerTransactionAddOn)
                        ).toFixed(2)
                      }
                    />
                  </Grid.Col>
                  {form.values.switchId === 6 ? (
                    <Grid.Col xs={12} md={6}>
                      <UploadButton
                        label={label.pemFile}
                        currentUploadedFiles={getFiles()}
                        addListItem={(droppedFiles: File[]) => {
                          let formattedFiles: UpdateFormFile[] =
                            droppedFiles.map((file) => {
                              return {
                                id: null,
                                file: file,
                              };
                            });

                          form.setFieldValue(
                            'pemFile',
                            formList([...formattedFiles])
                          );

                          setShowPEMError(false);
                        }}
                        removeListItem={(index) => {
                          form.removeListItem('pemFile', index);
                        }}
                      />
                      {showPEMError ? (
                        <Text color='red'>Please upload PEM file</Text>
                      ) : (
                        <></>
                      )}
                    </Grid.Col>
                  ) : (
                    <></>
                  )}
                </Grid>
                {getDynamicFilter()}
              </>
            )}
            <Divider my='lg' />
            <Group position='apart'>
              <Button color='gray' disabled={isInitialLoading}>
                Clear Form
              </Button>
              <Button
                type='submit'
                loading={isLoading}
                disabled={isInitialLoading}
              >
                Submit
              </Button>
            </Group>
          </Container>
        </form>
      </Paper>
    </div>
  );
}

export default UpdateMIDs;

interface Props {
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  midId: number;
  setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
}

const placeHolder = {
  partner: 'Select a partner',
  reseller: 'Select a reseller',
  merchant: 'Select a merchant',
  midName: 'MID name',

  switch: 'Select Switch',
  cardType: 'Select card type',
  currency: 'Select currency',

  param3: 'Secure Hash',
  param2: 'Merchant ID',
  param6: 'Terminal ID',
  mcc: 'MCC',

  xpayMarkup: 'Xpay mark up',
  acquirerBuyRate: 'Acquirer Buy Rate',
  decline: 'Decline',

  xpayPerTransactionAddOn: 'Per Transaction Add-on (XPAY)',
  clientPerTransactionAddOn: 'Per Transaction Add-on (Client)',
  fraudControlFee: 'Fraud control fee',
  preAuthRate: 'Pre auth rate',
  captureRate: 'Capture rate',
  finalMerchantRate: 'Final merchant rate',

  clientMarkup: ' Client mark up',
  refundRate: 'Refund rate',
  voidRate: 'Void rate',

  cashBackRate: 'Cash back rate',
  balanceInquiryRate: 'Balance inquiry rate',
};
const label = {
  partner: 'Select partner',
  reseller: 'Select reseller',
  merchant: 'Select merchant',
  midName: 'MID name',

  switch: 'Switch: ',
  cardType: 'Card type: ',
  currency: 'Currency: ',

  param3: 'Secure Hash',
  param2: 'Merchant ID',
  param6: 'Terminal ID',
  mcc: 'MCC',

  xpayMarkup: 'Xpay mark up',
  acquirerBuyRate: 'Acquirer Buy Rate',
  decline: 'Decline',

  xpayPerTransactionAddOn: 'Per Transaction Add-on (XPAY)',
  clientPerTransactionAddOn: 'Per Transaction Add-on (Client)',
  fraudControlFee: 'Fraud control fee',
  preAuthRate: 'Pre auth rate',
  captureRate: 'Capture rate',
  finalMerchantRate: 'Final merchant rate',

  clientMarkup: ' Client mark up',
  refundRate: 'Refund rate',
  voidRate: 'Void rate',

  cashBackRate: 'Cash back rate',
  balanceInquiryRate: 'Balance inquiry rate',
  pemFile: 'PEM file',
};
